import classNames from "classnames";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import {
	KeyboardEvent,
	PropsWithChildren,
	ReactElement,
	RefObject,
	useContext,
	useMemo,
	useRef,
	useState,
	FocusEvent,
	useCallback
} from "react";
import { ActionListItem } from "../ActionList/ActionListItem";
import {
	Button,
	ButtonKind,
	ButtonSize
} from "../Button/Button";
import { ItemResponsiveBehavior } from "../CollapsibleList/CollapsibleList";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import { FontIconName, SvgIconValue } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import {
	DropDownDirection,
	ToolbarContext,
	ToolbarPriority,
	buttonSizeToIconButtonSizeMap
} from "./Toolbar";
import styles from "./Toolbar.module.less";
import { ToolbarItem } from "./ToolbarItem";
import { mergeRefs } from "client/Misc/reactUtils";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { Key } from "client/Misc/commonTypes";

export type ToolbarSplitButtonProps = {
	kind: "primary" | "secondary";
	text: string;
	menuTitle: string;
	icon?: FontIconName | SvgIconValue;
	onClick(): void;
	isDisabled?: boolean;
	trackingId?: string;
	responsiveBehavior?: ItemResponsiveBehavior;
	dropOverWidth?: number;
};

export const ToolbarSplitButton = (props: PropsWithChildren<ToolbarSplitButtonProps>): ReactElement => {
	const {
		kind,
		text,
		menuTitle,
		icon,
		onClick,
		isDisabled = false,
		trackingId,
		responsiveBehavior,
		dropOverWidth,
		children
	} = props;

	const {
		dropDownDirection,
		buttonSize
	} = useContext(ToolbarContext);

	const priority = useMemo(
		(): number => {
			if (responsiveBehavior === "hide") {
				return ToolbarPriority.LowPriorityItem;
			}
			if (kind === "primary") {
				return ToolbarPriority.PrimaryButton;
			}

			return ToolbarPriority.Default;
		},
		[responsiveBehavior, kind]
	);

	return (
		<ToolbarItem
			isGroup
			priority={priority}
			responsiveBehavior={responsiveBehavior}
			isDisabled={isDisabled}
			onExecute={onClick}
			renderNormalMode={(
				ref,
				onKeyDown,
				tabIndex
			) => (
				<NormalMode
					ref={ref}
					onKeyDown={onKeyDown}
					tabIndex={tabIndex}
					kind={kind}
					icon={icon}
					onClick={onClick}
					isDisabled={isDisabled}
					text={text}
					dropDownDirection={dropDownDirection}
					dropOverWidth={dropOverWidth}
					trackingId={trackingId}
					buttonSize={buttonSize}
					menuTitle={menuTitle}
				>
					{children}
				</NormalMode>
			)}
			renderCollapsedMode={() => (
				<CollapsedMode
					onClick={onClick}
					text={text}
					icon={icon}
					isDisabled={isDisabled}
					trackingId={trackingId}
				>
					{children}
				</CollapsedMode>
			)}
		/>
	);
};
ToolbarSplitButton.displayName = "Toolbar.SplitButton";

type NormalProps = {
	ref: (element: HTMLElement | null) => void;
	onKeyDown: (args: KeyboardEvent<HTMLElement>) => void;
	tabIndex: number;
	kind: ("primary" | "secondary") & ButtonKind;
	text: string;
	menuTitle: string;
	icon: FontIconName | SvgIconValue | undefined;
	dropDownDirection: DropDownDirection;
	onClick(): void;
	isDisabled: boolean | undefined;
	dropOverWidth: number | undefined;
	trackingId: string | undefined;
	buttonSize: ButtonSize;
};

const NormalMode = (props: PropsWithChildren<NormalProps>): ReactElement => {
	const {
		ref,
		onKeyDown,
		tabIndex,
		kind,
		text,
		menuTitle,
		icon,
		dropDownDirection,
		onClick,
		isDisabled,
		trackingId,
		dropOverWidth,
		buttonSize,
		children
	} = props;

	const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const wrapperRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const chevronKeyDownHandler = useCallback(
		(event: KeyboardEvent<HTMLElement>): void => {
			const key: Key | null = tryResolveKeyboardKey(event);

			if (key === Key.Enter || key === Key.Space) {
				event.stopPropagation();
			}
		},
		[]
	);

	return (
		<div
			className={
				classNames(
					styles.splitButtonWrapper,
					{
						[styles.splitButtonWrapper_large]: buttonSize === "large"
					}
				)
			}
			ref={mergeRefs(ref, wrapperRef)}
			tabIndex={tabIndex}
			onKeyDown={onKeyDown}
			onFocus={() => setIsFocused(true)}
			onBlur={(e: FocusEvent<HTMLDivElement>): void => {
				if (!e.currentTarget.contains(e.relatedTarget)) {
					setIsFocused(false);
				}
			}}
		>
			<Button
				kind={kind}
				icon={icon}
				text={text}
				onClick={onClick}
				isDisabled={isDisabled}
				trackingId={generateTrackingId(trackingId, "_main-button")}
				attachedOn="right"
				size={buttonSize}
				tabIndex={-1}
			/>
			<div className={classNames(
				styles.splitButtonChevronWrapper
			)}>
				<IconButton
					kind={kind}
					icon="chevron-down"
					shape="round"
					attachedOn="left"
					trackingId={generateTrackingId(trackingId, "_arrow")}
					onClick={() => setIsDropDownOpen(true)}
					isDisabled={isDisabled}
					size={buttonSizeToIconButtonSizeMap[buttonSize]}
					bubbleClick={false}
					tabIndex={isFocused ? 0 : -1}
					onKeyDown={chevronKeyDownHandler}
				/>
				{isDropDownOpen && !isDisabled && (
					<DropOverActionList
						target={ensureNotNil(wrapperRef.current)}
						contentAlignment={
							dropDownDirection === "down"
								? "tl"
								: "bl"
						}
						targetAlignment={
							dropDownDirection === "down"
								? "bl"
								: "tl"
						}
						onClose={() => setIsDropDownOpen(false)}
						width={dropOverWidth}
						menuTitle={menuTitle}
					>
						{children}
					</DropOverActionList>
				)}
			</div>
		</div>
	);
};
NormalMode.displayName = "NormalMode";

type CollapsedModeProps = {
	text: string;
	icon: FontIconName | SvgIconValue | undefined;
	onClick(): void;
	isDisabled: boolean | undefined;
	trackingId: string | undefined;
};

const CollapsedMode = (props: PropsWithChildren<CollapsedModeProps>): ReactElement => {
	const {
		text,
		icon,
		onClick,
		isDisabled,
		trackingId,
		children
	} = props;

	return (
		<>
			<ActionListItem
				icon={icon}
				text={text}
				onClick={onClick}
				trackingId={trackingId}
				isDisabled={isDisabled}
			/>
			{!isDisabled && children}
		</>
	);
};
CollapsedMode.displayName = "CollapsedMode";